import type { NovaPoshtaReferenceSelection, SupportedLocale, WarehouseKind } from "./nova-poshta-reference-v1.js";
import { NP_REFERENCE_API } from "./nova-poshta-reference-v1.js";
import { normalizeDirectoryQuery } from "./nova-poshta-combobox-replace.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") ?? "";

function url(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return API_BASE ? `${API_BASE}${p}` : p;
}

export function isNovaPoshtaReferenceCheckoutEnabled(): boolean {
  // Staging Pages MUST bake VITE_NOVA_POSHTA_REFERENCE_DATA_V1=true (see staging-api-base.guard).
  // Empty/unset → false so production builds never silently enable NP reference UI.
  const v = String(import.meta.env.VITE_NOVA_POSHTA_REFERENCE_DATA_V1 ?? "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

export function isNovaPoshtaAddressPreparedEnabled(): boolean {
  const v = String(import.meta.env.VITE_NOVA_POSHTA_ADDRESS_PREPARED_V1 ?? "true").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

export type SettlementOption = {
  settlementRef: string;
  cityRef: string | null;
  label: string;
  region: string | null;
  area: string | null;
  settlementType: string | null;
};

export type WarehouseOption = {
  warehouseRef: string;
  label: string;
  shortAddress: string;
  number: string | null;
  kind: WarehouseKind | string;
  maxWeightKg: number | null;
};

export type NovaPoshtaDirectoryResult<T> = {
  ok: boolean;
  items: T[];
  message?: string;
  rateLimited?: boolean;
  retryAfterSec?: number | null;
  page?: number;
  limit?: number;
  aborted?: boolean;
};

/** In-flight dedupe — identical normalized queries share one network call. */
const settlementInflight = new Map<string, Promise<NovaPoshtaDirectoryResult<SettlementOption>>>();
const warehouseInflight = new Map<string, Promise<NovaPoshtaDirectoryResult<WarehouseOption> & { page: number; limit: number }>>();

/** Short session cache — avoid repeat identical upstream after success. */
const settlementCache = new Map<string, { at: number; result: NovaPoshtaDirectoryResult<SettlementOption> }>();
const warehouseCache = new Map<
  string,
  { at: number; result: NovaPoshtaDirectoryResult<WarehouseOption> & { page: number; limit: number } }
>();
const SETTLEMENT_CACHE_TTL_MS = 10 * 60 * 1000;
const WAREHOUSE_CACHE_TTL_MS = 5 * 60 * 1000;

function parseRetryAfter(res: Response, json: { retry_after_sec?: number }): number | null {
  const header = res.headers.get("Retry-After");
  if (header && /^\d+$/.test(header.trim())) return Number(header.trim());
  if (typeof json.retry_after_sec === "number" && json.retry_after_sec > 0) return json.retry_after_sec;
  return null;
}

function isRateLimitedResponse(
  res: Response,
  json: { code?: string; message?: string },
): boolean {
  if (res.status === 429) return true;
  if (json.code === "nova_poshta_rate_limited") return true;
  const msg = String(json.message ?? "");
  return /to many|too many|rate|обмежил/i.test(msg);
}

export async function searchNovaPoshtaSettlements(
  q: string,
  locale: SupportedLocale,
  signal?: AbortSignal,
): Promise<NovaPoshtaDirectoryResult<SettlementOption>> {
  if (q.trim().length < 2) return { ok: true, items: [] };
  const key = `s:${locale}:${normalizeDirectoryQuery(q)}`;
  const cached = settlementCache.get(key);
  if (cached && Date.now() - cached.at < SETTLEMENT_CACHE_TTL_MS) {
    return cached.result;
  }
  const existing = settlementInflight.get(key);
  if (existing) {
    return existing.then((r) => {
      if (signal?.aborted) return { ok: true, items: [] };
      return r;
    });
  }

  const pending = (async (): Promise<NovaPoshtaDirectoryResult<SettlementOption>> => {
    try {
      const res = await fetch(
        url(`${NP_REFERENCE_API.searchSettlements}?q=${encodeURIComponent(q)}&locale=${locale}`),
        {
          headers: { Accept: "application/json" },
          ...(signal ? { signal } : {}),
        },
      );
      const json = (await res.json()) as {
        items?: Array<{
          settlementRef: string;
          cityRef?: string | null;
          label: string;
          region?: string | null;
          area?: string | null;
          settlementType?: string | null;
        }>;
        message?: string;
        code?: string;
        retry_after_sec?: number;
      };
      if (isRateLimitedResponse(res, json)) {
        return {
          ok: false,
          items: [],
          rateLimited: true,
          retryAfterSec: parseRetryAfter(res, json) ?? 5,
          ...(json.message ? { message: json.message } : {}),
        };
      }
      if (!res.ok) return { ok: false, items: [], ...(json.message ? { message: json.message } : {}) };
      const result: NovaPoshtaDirectoryResult<SettlementOption> = {
        ok: true,
        items: (json.items ?? []).map((i) => ({
          settlementRef: i.settlementRef,
          cityRef: i.cityRef ?? i.settlementRef,
          label: i.label,
          region: i.region ?? null,
          area: i.area ?? null,
          settlementType: i.settlementType ?? null,
        })),
      };
      settlementCache.set(key, { at: Date.now(), result });
      return result;
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return { ok: true, items: [] };
      return { ok: false, items: [], message: "network" };
    } finally {
      settlementInflight.delete(key);
    }
  })();

  settlementInflight.set(key, pending);
  return pending;
}

export async function listNovaPoshtaWarehouses(input: {
  settlementRef: string;
  /** Preferred CityRef when settlement entity Ref differs — CUSTOMER-R4.15. */
  cityRef?: string | null;
  kind?: "branch" | "parcel_locker" | "all_branches";
  q?: string;
  locale: SupportedLocale;
  page?: number;
  limit?: number;
  signal?: AbortSignal;
}): Promise<NovaPoshtaDirectoryResult<WarehouseOption> & { page: number; limit: number }> {
  // Prefer cityRef for NP getWarehouses (CityRef); fall back to settlementRef.
  const cityKey = String(input.cityRef || input.settlementRef).trim();
  const params = new URLSearchParams({
    settlement_ref: cityKey,
    locale: input.locale,
    limit: String(input.limit ?? 30),
    page: String(input.page ?? 1),
  });
  if (input.kind === "parcel_locker") params.set("kind", "parcel_locker");
  else if (input.kind === "branch") params.set("kind", "branch");
  if (input.q?.trim()) params.set("q", input.q.trim());

  const key = `w:${cityKey}:${input.kind ?? "all"}:${normalizeDirectoryQuery(input.q ?? "")}:p${input.page ?? 1}`;
  const cached = warehouseCache.get(key);
  if (cached && Date.now() - cached.at < WAREHOUSE_CACHE_TTL_MS) {
    if (input.signal?.aborted) {
      return {
        ok: false,
        aborted: true,
        items: [],
        page: input.page ?? 1,
        limit: input.limit ?? 30,
      };
    }
    return cached.result;
  }
  // Never share inflight across user-typed AbortSignals — a stale abort
  // would otherwise return an empty list as a successful result.
  const existing = input.signal ? undefined : warehouseInflight.get(key);
  if (existing) {
    return existing;
  }

  const pending = (async () => {
    try {
      const res = await fetch(url(`${NP_REFERENCE_API.listWarehouses}?${params}`), {
        headers: { Accept: "application/json" },
        ...(input.signal ? { signal: input.signal } : {}),
      });
      const json = (await res.json()) as {
        items?: WarehouseOption[];
        page?: number;
        limit?: number;
        message?: string;
        code?: string;
        retry_after_sec?: number;
      };
      if (isRateLimitedResponse(res, json)) {
        return {
          ok: false,
          items: [] as WarehouseOption[],
          page: input.page ?? 1,
          limit: input.limit ?? 30,
          rateLimited: true,
          retryAfterSec: parseRetryAfter(res, json) ?? 5,
          ...(json.message ? { message: json.message } : {}),
        };
      }
      if (!res.ok) {
        return {
          ok: false,
          items: [] as WarehouseOption[],
          page: input.page ?? 1,
          limit: input.limit ?? 30,
          ...(json.message ? { message: json.message } : {}),
        };
      }
      let items = json.items ?? [];
      if (input.kind === "all_branches") {
        items = items.filter((w) => w.kind !== "parcel_locker");
      }
      const result = {
        ok: true as const,
        items,
        page: json.page ?? input.page ?? 1,
        limit: json.limit ?? input.limit ?? 30,
      };
      warehouseCache.set(key, { at: Date.now(), result });
      return result;
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") {
        return {
          ok: false,
          aborted: true,
          items: [] as WarehouseOption[],
          page: input.page ?? 1,
          limit: input.limit ?? 30,
        };
      }
      return {
        ok: false,
        items: [] as WarehouseOption[],
        page: input.page ?? 1,
        limit: input.limit ?? 30,
        message: "network",
      };
    } finally {
      warehouseInflight.delete(key);
    }
  })();

  warehouseInflight.set(key, pending);
  return pending;
}

export type ValidateNovaPoshtaResult = {
  ok: boolean;
  errors: Array<{ code: string; message: string }>;
  rateLimited?: boolean;
  retryAfterSec?: number | null;
};

export async function validateNovaPoshtaSelection(
  sel: NovaPoshtaReferenceSelection,
): Promise<ValidateNovaPoshtaResult> {
  const mode =
    sel.mode === "parcel_locker" ? "parcel_locker" : sel.mode === "address" ? "address" : "warehouse";
  try {
    const res = await fetch(url(NP_REFERENCE_API.validate), {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({
        mode,
        settlementRef: sel.settlementRef,
        cityRef: sel.cityRef,
        warehouseRef: sel.warehouseRef,
        streetRef: sel.streetRef,
        addressLine: sel.addressLine,
        building: sel.building,
        flat: sel.flat,
        locale: sel.locale,
      }),
    });
    const json = (await res.json()) as {
      ok?: boolean;
      errors?: Array<{ code: string; message: string }>;
      message?: string;
      code?: string;
      retry_after_sec?: number;
    };
    if (isRateLimitedResponse(res, json)) {
      return {
        ok: false,
        rateLimited: true,
        retryAfterSec: parseRetryAfter(res, json) ?? 5,
        errors: [
          {
            code: "nova_poshta_rate_limited",
            message:
              sel.locale === "en"
                ? "Nova Poshta temporarily limited requests. Wait a few seconds — your city and branch selection is kept."
                : "Нова Пошта тимчасово обмежила кількість запитів. Зачекайте кілька секунд. Ваш вибір міста та відділення збережено.",
          },
        ],
      };
    }
    if (res.ok && json.ok) return { ok: true, errors: [] };
    const errors = (json.errors ?? []).map((e) => ({
      code: e.code,
      message: sanitizeClientValidationMessage(e.code, e.message),
    }));
    if (!errors.length && json.message) {
      errors.push({ code: "provider_error", message: sanitizeClientValidationMessage("provider_error", json.message) });
    }
    if (!errors.length) {
      errors.push({
        code: "validation_failed",
        message:
          sel.locale === "en"
            ? "Select a city from the list."
            : "Оберіть місто або населений пункт зі списку.",
      });
    }
    return { ok: false, errors };
  } catch {
    return {
      ok: false,
      errors: [
        {
          code: "network",
          message:
            sel.locale === "en"
              ? "Could not verify address. Retry or use manual entry."
              : "Не вдалося перевірити адресу. Спробуйте ще раз або оберіть ручне введення.",
        },
      ],
    };
  }
}

function sanitizeClientValidationMessage(code: string, message: string): string {
  if (code === "settlement_not_found" || code === "settlement_required" || /settlement was not found/i.test(message)) {
    return "Оберіть місто або населений пункт зі списку.";
  }
  if (/street is required|building is required/i.test(message)) {
    return "Заповніть адресу доставки або оберіть відділення.";
  }
  if (code === "nova_poshta_rate_limited" || /to many|too many|rate|обмежил/i.test(message)) {
    return "Нова Пошта тимчасово обмежила кількість запитів. Зачекайте кілька секунд. Ваш вибір міста та відділення збережено.";
  }
  if (/[A-Za-z]{4,}/.test(message) && !/[А-Яа-яЇїІіЄєҐґ]/.test(message)) {
    return "Не вдалося перевірити адресу. Спробуйте ще раз або оберіть ручне введення.";
  }
  return message;
}

import {
  isCheckoutDeliverySelectionConsistent,
  looksLikeOfficialNovaPoshtaRef,
} from "./nova-poshta-combobox-replace.js";

export function selectionToCheckoutPayload(
  sel: NovaPoshtaReferenceSelection,
): Record<string, unknown> {
  const deliveryType =
    sel.mode === "parcel_locker" ? "parcel_locker" : sel.mode === "address" ? "address" : "warehouse";
  const locationType =
    sel.mode === "parcel_locker" ? "postomat" : sel.mode === "address" ? "address" : "warehouse";
  const warehouseLabel =
    sel.mode === "address"
      ? sel.warehouseLabel ?? sel.addressLine ?? "Адресна доставка"
      : sel.warehouseLabel ?? "";
  const warehouseAddress = sel.warehouseAddress ?? warehouseLabel;
  const displayValue = [sel.settlementLabel, warehouseLabel].filter(Boolean).join(", ").slice(0, 240);
  const cityRef = sel.cityRef ?? sel.settlementRef;
  return {
    schema: "117a-r1",
    delivery_provider: "nova_poshta",
    delivery_method: "nova_poshta",
    settlement_ref: sel.settlementRef,
    city_ref: cityRef,
    settlement_name: sel.settlementLabel,
    region: sel.region ?? undefined,
    area: sel.area ?? undefined,
    settlement_type: sel.settlementType ?? undefined,
    delivery_type: deliveryType,
    warehouse_ref: sel.warehouseRef,
    warehouse_number: sel.warehouseNumber ?? undefined,
    warehouse_label: warehouseLabel || undefined,
    warehouse_name: warehouseLabel || undefined,
    warehouse_address: warehouseAddress || undefined,
    location_type: locationType,
    // COMMERCE-CANARY-163A-R2A — canonical checkout NP field aliases.
    nova_poshta_city_ref: cityRef,
    nova_poshta_city_name: sel.settlementLabel,
    nova_poshta_area_name: sel.area ?? sel.region ?? undefined,
    nova_poshta_warehouse_ref: sel.warehouseRef,
    nova_poshta_warehouse_number: sel.warehouseNumber ?? undefined,
    nova_poshta_warehouse_name: warehouseLabel || undefined,
    nova_poshta_warehouse_address: warehouseAddress || undefined,
    nova_poshta_location_type: locationType,
    display_value: displayValue,
    display_suffix: warehouseLabel || undefined,
    address_line: sel.addressLine,
    street_ref: sel.streetRef,
    building: sel.building,
    flat: sel.flat,
    locale: sel.locale,
    provider_source_revision: sel.version,
    provider_validated_at: new Date().toISOString(),
    selected_at: new Date().toISOString(),
    provider_mode: "live_readonly",
    ttn_state: "not_created",
  };
}

export { isCheckoutDeliverySelectionConsistent, looksLikeOfficialNovaPoshtaRef };

export function selectionDisplayCity(sel: NovaPoshtaReferenceSelection | null): string {
  return sel?.settlementLabel ?? "";
}

export function selectionDisplayWarehouse(sel: NovaPoshtaReferenceSelection | null): string {
  if (!sel) return "";
  if (sel.mode === "address") return sel.warehouseLabel ?? sel.addressLine ?? "Адресна доставка";
  return sel.warehouseLabel ?? "";
}

/** Test helpers */
export function __resetNovaPoshtaClientCachesForTests(): void {
  settlementInflight.clear();
  warehouseInflight.clear();
  settlementCache.clear();
  warehouseCache.clear();
}
